import { useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { 
  X, 
  CalendarClock, 
  Wallet,
  Check
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

// Get default date for scheduling (tomorrow)
const getDefaultDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const SchedulePaymentModal = ({ biller, onClose }) => {
  const queryClient = useQueryClient();
  const [scheduleType, setScheduleType] = useState("once");
  const [amount, setAmount] = useState(biller.outstanding_balance?.toString() || "");
  const [scheduledDate, setScheduledDate] = useState(getDefaultDate);
  const [dayOfWeek, setDayOfWeek] = useState(0);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [step, setStep] = useState(1);

  const weekdays = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  // Fetch wallets
  const { data: wallets } = useQuery({
    queryKey: ["wallets"],
    queryFn: async () => {
      const response = await axios.get(`${API}/wallets`);
      return response.data;
    }
  });

  // Get default wallet (JMD preferred)
  const defaultWalletId = useMemo(() => {
    if (!wallets?.length) return null;
    const jmdWallet = wallets.find(w => w.currency === "JMD");
    return jmdWallet?.wallet_id || wallets[0].wallet_id;
  }, [wallets]);

  const [selectedWallet, setSelectedWallet] = useState(null);
  const effectiveWallet = selectedWallet || defaultWalletId;

  // Create schedule mutation
  const scheduleMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        biller_id: biller.biller_id,
        amount: parseFloat(amount),
        wallet_id: effectiveWallet,
        schedule_type: scheduleType,
        scheduled_date: scheduleType === "once" ? scheduledDate : null,
        day_of_week: scheduleType === "weekly" ? dayOfWeek : null,
        day_of_month: scheduleType === "monthly" ? dayOfMonth : null
      };
      const response = await axios.post(`${API}/bills/schedule`, payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["scheduledPayments"]);
      setStep(2);
    },
    onError: (error) => {
      toast.error(error.response?.data?.detail || "Failed to schedule payment");
    }
  });

  const selectedWalletData = wallets?.find(w => w.wallet_id === effectiveWallet);

  const formatCurrency = (amount, currency = "JMD") => {
    const symbols = { JMD: "J$", USD: "$", GBP: "£", CAD: "C$" };
    return `${symbols[currency] || currency}${amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
  };

  const getScheduleDescription = () => {
    if (scheduleType === "once") {
      return `on ${new Date(scheduledDate).toLocaleDateString()}`;
    } else if (scheduleType === "weekly") {
      return `every ${weekdays[dayOfWeek]}`;
    } else if (scheduleType === "monthly") {
      const suffix = dayOfMonth === 1 ? "st" : dayOfMonth === 2 ? "nd" : dayOfMonth === 3 ? "rd" : "th";
      return `on the ${dayOfMonth}${suffix} of each month`;
    }
    return "";
  };

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div 
        className="w-full max-w-lg bg-card/95 backdrop-blur-2xl rounded-t-3xl sm:rounded-3xl 
          border-t sm:border border-white/10 p-6 animate-slide-up max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="schedule-modal"
      >
        {step === 1 && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading font-semibold text-lg">Schedule Payment</h3>
              <button 
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center text-muted-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Biller Info */}
              <div className="glass rounded-2xl p-4">
                <p className="font-medium text-foreground">{biller.nickname || biller.provider_name}</p>
                <p className="text-xs text-muted-foreground">{biller.masked_account}</p>
              </div>

              {/* Amount */}
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">Payment amount</p>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-muted-foreground">
                    J$
                  </span>
                  <Input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="input-glass pl-12 text-xl font-mono h-14"
                    data-testid="schedule-amount-input"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-secondary text-xs"
                    onClick={() => setAmount(biller.outstanding_balance.toString())}
                  >
                    Full Amount
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="btn-secondary text-xs"
                    onClick={() => setAmount((biller.outstanding_balance / 2).toFixed(2))}
                  >
                    Half
                  </Button>
                </div>
              </div>

              {/* Schedule Type */}
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Schedule type</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: "once", label: "One-time" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "Monthly" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setScheduleType(type.value)}
                      className={`p-3 rounded-xl border text-sm ${
                        scheduleType === type.value
                          ? "border-primary bg-primary/10"
                          : "border-white/10 bg-white/5"
                      }`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Schedule Details */}
              {scheduleType === "once" && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Payment date</p>
                  <Input
                    type="date"
                    value={scheduledDate}
                    onChange={(e) => setScheduledDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="input-glass"
                    data-testid="schedule-date-input"
                  />
                </div>
              )}

              {scheduleType === "weekly" && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Day of week</p>
                  <div className="grid grid-cols-4 gap-2">
                    {weekdays.map((day, index) => (
                      <button
                        key={day}
                        onClick={() => setDayOfWeek(index)}
                        className={`p-2 rounded-xl border text-xs ${
                          dayOfWeek === index
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-white/5"
                        }`}
                      >
                        {day.slice(0, 3)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {scheduleType === "monthly" && (
                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">Day of month</p>
                  <div className="grid grid-cols-7 gap-1">
                    {Array.from({ length: 28 }, (_, i) => i + 1).map((day) => (
                      <button
                        key={day}
                        onClick={() => setDayOfMonth(day)}
                        className={`p-2 rounded-lg border text-xs ${
                          dayOfMonth === day
                            ? "border-primary bg-primary/10"
                            : "border-white/10 bg-white/5"
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Wallet Selection */}
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">Pay from</p>
                <div className="space-y-2">
                  {wallets?.filter(w => w.currency === "JMD").map((wallet) => (
                    <button
                      key={wallet.wallet_id}
                      onClick={() => setSelectedWallet(wallet.wallet_id)}
                      className={`w-full p-3 rounded-xl border flex items-center gap-3 ${
                        effectiveWallet === wallet.wallet_id
                          ? "border-primary bg-primary/10"
                          : "border-white/10 bg-white/5"
                      }`}
                    >
                      <Wallet className="w-5 h-5 text-muted-foreground" />
                      <div className="flex-1 text-left">
                        <p className="text-sm font-medium">{wallet.currency} Wallet</p>
                        <p className="text-xs text-muted-foreground">
                          Balance: {formatCurrency(wallet.available_balance, wallet.currency)}
                        </p>
                      </div>
                      {effectiveWallet === wallet.wallet_id && (
                        <Check className="w-4 h-4 text-primary" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="glass rounded-2xl p-4">
                <p className="text-xs text-muted-foreground mb-2">Payment Summary</p>
                <p className="text-sm text-foreground">
                  {formatCurrency(parseFloat(amount) || 0)} will be paid {getScheduleDescription()}
                </p>
              </div>

              {/* Submit */}
              <Button
                className="w-full btn-primary h-12"
                onClick={() => scheduleMutation.mutate()}
                disabled={scheduleMutation.isPending || !amount || parseFloat(amount) <= 0}
                data-testid="create-schedule-btn"
              >
                {scheduleMutation.isPending ? "Scheduling..." : "Schedule Payment"}
              </Button>
            </div>
          </>
        )}

        {/* Success Step */}
        {step === 2 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4">
              <CalendarClock className="w-8 h-8 text-primary" />
            </div>
            <h3 className="font-heading font-semibold text-lg mb-2">Payment Scheduled!</h3>
            <p className="text-sm text-muted-foreground mb-6">
              {formatCurrency(parseFloat(amount))} will be paid to {biller.provider_name} {getScheduleDescription()}.
            </p>
            <Button
              className="w-full btn-primary h-12"
              onClick={onClose}
            >
              Done
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SchedulePaymentModal;
